<script>
import axios from 'axios';
import { mask } from 'vue-the-mask';
import ClipboardJS from 'clipboard';
import dayjs from '../core/dayjs';
import IconCopy from './icon/Copy';
import '../../sass/vendor/swal2.scss';
import { Alert } from '../services/alert';
import Bankslip from './billing/Bankslip.vue'
import CheckCircle from './icon/CheckCircle.vue'
import { CheckoutCard } from '../integrations/components';
import { currencyFormat } from '../helpers/currencyFormat';

/**
 * Array of priority of errors
 */
const priorityErrors = ['number', 'expiration', 'verification_value'];

export default {
  components: {
    IconCopy,
    Bankslip,
    CheckoutCard,
    CheckCircle,
  },

  directives: {
    mask,
  },

  props: {
    invoice: {
      type: Object,
      required: true,
    },
  },

  mounted() {
    const { type, checkout } = this.invoice;

    if (type === 'checkout') {
      this.installments = checkout.installment_options;
    }

    this.test = window.BulldeskSettings.test;

    new ClipboardJS('.bd-copy').on('success', ({ trigger }) => {
      if (trigger.id === 'pix') {
        Alert.success(this.__('Pix copiado!'));

        return;
      }

      Alert.success(this.__('Código de barras copiado!'));
    });
  },

  data: () => ({
    test: null,
    error: null,
    status: null,
    loading: null,
    disabled: null,
    installment: 1,
    installments: 1,
    paymentSuccessful: false,

    credit_card: {
      name: '',
      number: '',
      validity: '',
      security_code: '',
    },

    cardErrors: {
      //
    },

    path: 'https://pay.salesflowcrm.com.br',
  }),

  computed: {
    button() {
      return {
        paid: 'Fatura Paga',
        expired: 'Fatura Expirada',
        pending: 'Pagar Agora',
        refunded: 'Fatura Estornada',
        canceled: 'Fatura Cancelada',
      };
    },

    total() {
      return currencyFormat(this.invoice.total / this.installment);
    },

    price() {
      return currencyFormat(this.invoice.total, 2, 'decimal');
    },

    dueAt() {
      return dayjs(this.invoice.due_at).format('DD/MM/YYYY');
    },

    crratedAt() {
      return dayjs(this.invoice.created_at).format('DD/MM/YYYY');
    },

    address() {
      const {
        address: { city = '', state = '', number = '', street = '' },
      } = this.invoice.metadata.customer || {};

      return `${street}, ${number}, ${city}/${state}`;
    },

    /**
     * Get error of card based on prioritys
     */
    cardError() {
      const { cardErrors } = this;

      const error = priorityErrors.find((priority) => cardErrors[priority]);

      return cardErrors[error] === 'is_invalid'
        ? this.__('Preencha todos os campos do cartão')
        : cardErrors[error];
    },

    /**
     * Get brand of card
     */
    cardBrand() {
      return Iugu.utils.getBrandByCreditCardNumber(
        this.credit_card.number.replace(/\D/g, ''),
      );
    },

    invoiceLast() {
      const { id } = this.invoice;

      if (!id) {
        return null;
      }

      const [first] = id.split('-').reverse();

      return first;
    },

    customerEmail () {
      return this.invoice
        .metadata
        .customer
        .email
        .split(',')[0];
    },
  },

  methods: {
    clearErrors() {
      /**
       * reset errors
       */
      this.error = null;
      this.cardErrors = {};
    },

    pixLoaded(e) {
      console.log(e);
    },

    formatValue(value) {
      return currencyFormat(value / 100);
    },

    creditCardPayment() {
      /**
       * Return if has error on card
       */
      if (this.cardError && this.cardError !== 'is_invalid') {
        console.log('has card errors', this.cardError);

        this.loading = null;
        Alert.error(this.cardError);

        return;
      }

      this.clearErrors();

      Iugu.setAccountID(window.BulldeskSettings.iuguAccountId);

      Iugu.setup();

      if (window.BulldeskSettings.test) {
        console.log('Test mode activated');

        Iugu.setTestMode(true);
      }

      const names = this.credit_card.name.split(' ');

      const [first = '', second = ''] =
        this.credit_card.validity.replace(/\D/g, '').match(/.{1,2}/g) || '';

      /**
       * Get a iugu token of card
       */
      const cc = Iugu.CreditCard(
        this.credit_card.number.replace(/\D/g, ''),
        first,
        second,
        names[0],
        names[1],
        this.credit_card.security_code,
      );

      /**
       * Callback for 'create-payment-token'
       */
      const tokenResponseHandler = (data) => {
                if (data.errors) {
          this.cardErrors = data.errors || {};

          this.loading = null;

          return;
        }

        const payload = {
          iugu_token: data.id,
          type: this.invoice.type,
          installments: this.installment,
          token: this.invoice.tenant.public_token,
        };

        this.send(payload);
      };

      Iugu.createPaymentToken(cc, tokenResponseHandler);
    },

    validate(value) {
      if (value.length < 15) {
        return;
      }

      const code = this.credit_card.security_code;

      if (code.length === 3) {
        const verificationValue = !Iugu.utils.validateCVV(code, this.cardBrand)
          ? 'CVC do cartão inválido.'
          : null;

        this.cardErrors = {
          ...this.cardErrors,
          verification_value: verificationValue,
        };
      }
    },

    /**
     * Submit
     */
    submit() {
      this.loading = this.__('Processando...');

      this.creditCardPayment();
    },

    send(payload) {
      axios
        .post(
          `${this.test || this.path}/${this.invoice.id}`,
          payload,
          { headers: { 'X-CSRF-TOKEN': window.BulldeskSettings.csrfToken } }
        )
        .then(({ data: { status } }) => {
          this.loading = null;

          this.disabled = 'Pagamento efetuado!'

          Alert.success('Pagamento efetuado com sucesso!');

          this.status = status;

          this.paymentSuccessful = true;
        })
        .catch(
          (error) => {
            console.error(error);

            this.loading = null;

            if (error.response && error.response.data && error.response.status === 402) {
              const { message, errors } = error.response.data;

              this.error = errors ? errors[0] : message;
            } else {
              Alert.error('Ocorreu um erro inesperado ao realizar o pagamento');
            }
          },
        );
    },
  },

  watch: {
    /**
     * Watch on value of card number input and validate this
     */
    'credit_card.number': {
      handler(value) {
        if (value.length === 19) {
          const parsed = value.replace(/\D/g, '');

          const number = !Iugu.utils.validateCreditCardNumber(parsed)
            ? 'Número do cartão inválido.'
            : null;

          this.cardErrors = { ...this.cardErrors, number };

          return;
        }

        this.cardErrors.number = null;
      },

      immediate: false,
    },

    /**
     * Watch on value of card validity input and validate this
     */
    'credit_card.validity': {
      handler(value) {
        if (value.length === 7) {
          const parsed = value
            .replace(/\D/g, '')
            .match(/.{1,2}/g)
            .join('/');

          const expiration = !Iugu.utils.validateExpirationString(parsed)
            ? 'Validade do cartão inválida.'
            : null;

          this.cardErrors = { ...this.cardErrors, expiration };

          return;
        }
        this.cardErrors.expiration = null;
      },

      immediate: false,
    },

    /**
     * Watch on value of card VCC input and validate this
     */
    'credit_card.security_code': {
      handler(value) {
        if (value.length === 3 && this.credit_card.number.length) {
          const verificationValue = !Iugu.utils.validateCVV(
            value,
            this.cardBrand,
          )
            ? 'CVC do cartão inválido.'
            : null;

          this.cardErrors = {
            ...this.cardErrors,
            verification_value: verificationValue,
          };

          return;
        }
        this.cardErrors.verification_value = null;
      },

      immediate: false,
    },

    /**
     * Watch on value of card VCC input and validate this
     */
    'credit_card.name': {
      handler() {
        if (this.cardErrors.last_name || this.cardErrors.first_name) {
          this.cardErrors.last_name = null;
          this.cardErrors.first_name = null;
        }
      },

      immediate: false,
    },
  },
};
</script>

<template>
  <div v-if="!paymentSuccessful" class="bdk-payment bdk-preflight">
    <div
      class="
        bdk-absolute bdk-inset-0 bdk-h-12/12 bdk-bg-gray-100
        bdk-payment__bg
      "
    />

    <div class="bdk-bg-white-100 bdk-no-print">
      <div class="bdk-mt-8 bdk-bg-white sm:bdk-mt-6">
        <div class="bdk-relative">
          <div class="bdk-absolute bdk-inset-0 bdk-h-12/12 bdk-bg-gray-100" />

          <div
            class="
              bdk-relative bdk-max-w-screen-md bdk-mx-auto bdk-px-4
              sm:bdk-px-6
              lg:bdk-px-8
            "
          >
            <div
              class="
                bdk-max-w-lg
                bdk-mx-auto
                bdk-rounded-lg
                bdk-shadow-lg
                bdk-overflow-hidden
                lg:bdk-max-w-none lg:bdk-flex
              "
            >
              <div
                class="
                  bdk-flex-4
                  bdk-bg-white
                  bdk-p-8
                  bdk-flex-1
                  bdk-flex
                  bdk-flex-col
                "
              >
                <div class="bdk-text-xs bdk-text-gray-500 bdk-font-medium">
                  <span v-text="__('Fatura - ')" />

                  <span v-text="invoiceLast" />
                </div>

                <p
                  class="bdk-mt-5 bdk-text-2xl bdk-text-gray-900"
                  v-text="invoice.description"
                />

                <div
                  v-if="invoice.type === 'checkout'"
                  class="
                    bdk-text-sm
                    bdk-text-gray-500
                    bdk-flex
                    bdk-flex-col
                    bdk-uppercase
                    bdk-mt-auto
                  "
                >
                  {{ invoice.tenant.company_name }}

                  <input
                    readonly
                    :value="invoice.tenant.document"
                    v-mask="['###.###.###-##', '##.###.###/####-##']"
                    class="
                      bdk-text-xs
                      bdk-text-gray-500
                      bdk-font-medium
                      bdk-outline-none
                    "
                  />
                </div>
              </div>

              <div
                class="
                  bdk-text-center bdk-relative bdk-bg-gray-50
                  lg:bdk-flex-shrink-0
                  lg:bdk-flex
                  lg:bdk-flex-col
                  lg:bdk-justify-center
                  bdk-p-8
                "
              >
                <div
                  class="
                    bdk-flex
                    bdk-text-base
                    bdk-items-center
                    bdk-justify-center
                    bdk-ml-auto
                  "
                >
                  <div
                    v-text="__('Vencimento')"
                    class="
                      bdk-leading-6 bdk-mr-2 bdk-font-medium bdk-text-gray-900
                    "
                  />

                  <div v-text="dueAt" class="bdk-text-gray-500" />
                </div>

                <div
                  class="bdk-mt-4 bdk-flex bdk-items-start bdk-justify-center"
                >
                  <span
                    class="
                      bdk-mt-1 bdk-text-lg bdk-font-medium bdk-text-gray-900
                    "
                  >
                    R$
                  </span>

                  <span
                    v-text="price"
                    class="bdk-text-5xl bdk-font-medium bdk-text-gray-900"
                  />
                </div>

                <div class="bdk-mt-6">
                  <div
                    class="bdk-rounded-md bdk-shadow"
                    v-if="invoice.status === 'pending'"
                  >
                    <a
                      href="#details"
                      v-text="__(button[invoice.status])"
                      class="
                        bdk-flex
                        bdk-items-center
                        bdk-justify-center
                        bdk-px-5
                        bdk-py-3
                        bdk-border
                        bdk-border-transparent
                        bdk-text-base
                        bdk-font-medium
                        bdk-rounded-md
                        bdk-text-white
                        bdk-bg-gray-800
                        hover:bdk-bg-gray-900
                      "
                    />
                  </div>

                  <div
                    v-else
                    v-text="button[invoice.status]"
                    class="
                      bdk-inline-flex
                      bdk-items-center
                      bdk-px-3
                      bdk-py-1
                      bdk-rounded-full
                      bdk-text-sm
                      bdk-font-medium
                      bdk-leading-5
                    "
                    :class="{
                      'bdk-bg-green-100 bdk-text-green-800':
                        invoice.status === 'paid',
                      'bdk-bg-gray-200 bdk-text-gray-500':
                        invoice.status === 'canceled',
                      'bdk-bg-indigo-100 bdk-text-indigo-800': [
                        'expired',
                        'refunded',
                      ].includes(invoice.status),
                    }"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div id="details" class="bdk-bg-white-100">
      <div class="bdk-mt-8 bdk-bg-white bdk-pb-8 sm:bdk-mt-12 bdk-no-padding">
        <div class="bdk-relative">
          <div
            class="
              bdk-relative bdk-max-w-screen-md bdk-mx-auto bdk-px-4
              sm:bdk-px-6
              lg:bdk-px-8
            "
          >
            <div class="bdk-bg-white bdk-py-8 bdk-invoice-info">
              <div
                class="bdk-flex bdk-justify-between bdk-items-start bdk-mb-2"
              >
                <div
                  class="
                    bdk-text-xs bdk-text-xs bdk-text-gray-500 bdk-font-medium
                  "
                >
                  <span v-text="__('Identificação da fatura:')" />

                  <span v-text="invoice.id" />
                </div>

                <div
                  class="
                    bdk-text-xs bdk-text-xs bdk-text-gray-500 bdk-font-medium
                  "
                >
                  <span v-text="__('Data da emissão:')" />

                  <span v-text="crratedAt" />
                </div>
              </div>

              <h2
                v-text="__('Detalhes')"
                class="bdk-text-xl bdk-tracking-tight"
              />

              <div
                class="bdk-space-y-2 bdk-mt-2 bdk-divide-y bdk-divide-gray-200"
              >
                <div class="bdk-flex bdk-justify-between bdk-items-start bdk-no-print">
                  <div
                    v-text="__('Cliente')"
                    class="
                      bdk-mt-2
                      bdk-text-sm
                      bdk-leading-6
                      bdk-font-medium
                      bdk-text-gray-900
                    "
                  />

                  <div
                    v-if="invoice.type === 'checkout'"
                    class="
                      bdk-text-sm
                      bdk-mt-2
                      bdk-text-gray-500
                      bdk-flex
                      bdk-flex-col
                      bdk-items-end
                      bdk-uppercase
                    "
                  >
                    {{ invoice.metadata.customer.name }}

                    <input
                      readonly
                      :value="invoice.metadata.customer.document"
                      v-mask="['###.###.###-##', '##.###.###/####-##']"
                      class="
                        bdk-text-xs
                        bdk-text-gray-500
                        bdk-font-medium
                        bdk-text-right
                        bdk-outline-none
                      "
                    />
                  </div>

                  <div
                    v-else
                    class="
                      bdk-text-sm
                      bdk-mt-2
                      bdk-text-gray-500
                      bdk-flex
                      bdk-flex-col
                      bdk-items-end
                      bdk-uppercase
                    "
                  >
                    {{ invoice.tenant.company_name }}

                    <input
                      readonly
                      :value="invoice.tenant.document"
                      v-mask="['###.###.###-##', '##.###.###/####-##']"
                      class="
                        bdk-text-xs
                        bdk-text-gray-500
                        bdk-font-medium
                        bdk-text-right
                        bdk-outline-none
                      "
                    />
                  </div>
                </div>

                <div class="bdk-flex bdk-justify-between bdk-items-start bdk-no-print">
                  <div
                    v-text="__('Cedente')"
                    class="
                      bdk-text-sm
                      bdk-mt-2
                      bdk-leading-6
                      bdk-font-medium
                      bdk-text-gray-900
                    "
                  />

                  <div class="bdk-flex bdk-flex-col bdk-uppercase">
                    <div
                      class="
                        bdk-text-sm
                        bdk-mt-2
                        bdk-text-gray-500
                        bdk-uppercase
                        bdk-flex
                        bdk-flex-col
                        bdk-items-end
                      "
                    >
                      5LABS

                      <span class="bdk-text-xs">45.781.022/0001-74</span>
                    </div>
                  </div>
                </div>

                <div class="bdk-flex bdk-justify-between bdk-items-start bdk-no-print">
                  <div
                    v-text="__('Intermediador')"
                    class="
                      bdk-text-sm
                      bdk-mt-2
                      bdk-leading-6
                      bdk-font-medium
                      bdk-text-gray-900
                    "
                  />

                  <div class="bdk-flex bdk-flex-col bdk-uppercase">
                    <div
                      class="
                        bdk-text-sm
                        bdk-mt-2
                        bdk-text-gray-500
                        bdk-flex
                        bdk-uppercase
                        bdk-flex-col
                        bdk-items-end
                      "
                    >
                      Iugu Serviços na Internet SA

                      <span class="bdk-text-xs">15.111.975/0001-64</span>
                    </div>
                  </div>
                </div>

                <div class="bdk-flex bdk-justify-between bdk-items-start">
                  <div
                    v-text="__('Itens')"
                    class="
                      bdk-text-sm
                      bdk-mt-2
                      bdk-leading-6
                      bdk-font-medium
                      bdk-text-gray-900
                    "
                  />

                  <div class="bdk-mt-2 bdk-flex bdk-flex-col bdk-space-y-2">
                    <span
                      :key="index"
                      class="bdk-text-sm bdk-text-gray-500 bdk-text-right"
                      v-for="(
                        { quantity, description, price_cents }, index
                      ) in invoice.items"
                    >
                      {{ quantity }}x {{ description }} -
                      {{ formatValue(price_cents) }}
                    </span>
                  </div>
                </div>

                <div class="bdk-flex bdk-justify-between bdk-items-start">
                  <div
                    v-text="__('Total')"
                    class="
                      bdk-text-sm
                      bdk-mt-2
                      bdk-leading-6
                      bdk-font-medium
                      bdk-text-gray-900
                    "
                  />

                  <div class="bdk-mt-2 bdk-flex bdk-flex-col">
                    <span
                      v-text="`R$ ${price}`"
                      class="bdk-text-sm bdk-text-gray-500"
                    />
                  </div>
                </div>
              </div>
            </div>

            <template
              v-if="invoice.status === 'pending'"
            >
              <div
                id="pix"
                v-if="invoice.methods.pix"
                class="bdk-bg-white bdk-py-8 bdk-no-print"
              >
                <h2 v-text="__('Pix')" class="bdk-text-xl bdk-tracking-tight" />

                <div class="bdk-flex bdk-mt-4 bdk-items-center">
                  <div class="bdk-payment__pix bdk-flex-shrink-0">
                    <div
                      class="bdk-flex-shrink-0 bdk-border bdk-border-gray-200"
                    >
                      <img
                        @load="pixLoaded"
                        :src="invoice.methods.pix.qrcode"
                        class="bdk-object-cover bdk-shadow-lg bdk-rounded-lg"
                      />
                    </div>

                    <div class="bdk-mt-2">
                      <div class="bdk-rounded-md bdk-shadow">
                        <a
                          id="pix"
                          v-text="__('Copiar Pix')"
                          :data-clipboard-text="invoice.methods.pix.qrcode_text"
                          class="
                            bd-copy
                            bdk-cursor-pointer
                            bdk-flex
                            bdk-items-center
                            bdk-justify-center
                            bdk-px-5
                            bdk-py-3
                            bdk-border
                            bdk-border-transparent
                            bdk-text-base
                            bdk-font-medium
                            bdk-rounded-md
                            bdk-text-white
                            bdk-bg-gray-800
                            hover:bdk-bg-gray-900
                          "
                        />
                      </div>
                    </div>
                  </div>

                  <div
                    class="
                      sm:bdk-col-span-2
                      bdk-space-y-4 bdk-flex bdk-flex-col
                    "
                  >
                    <p
                      class="bdk-text-base bdk-text-gray-500 bdk-text-center"
                      v-text="
                        __(
                          'O Pix é a nova modalidade de transferências do banco central, que funcionam 24 horas por dia e possuem confirmação em tempo real.',
                        )
                      "
                    />

                    <p
                      class="bdk-text-base bdk-text-gray-500 bdk-text-center"
                      v-text="
                        __(
                          'Procure em seu aplicativo de banco ou conta digital a funcionalidade e escaneie o QR Code ao lado ou copie o código usando o Pix Copia e Cola para efetuar o pagamento.',
                        )
                      "
                    />
                  </div>
                </div>
              </div>

              <div
                class="bdk-bg-white bdk-py-8 bdk-no-padding"
                v-if="invoice.methods.bankslip"
              >
                <h2
                  v-text="__('Boleto Bancário')"
                  class="bdk-text-xl bdk-tracking-tight"
                />

                <Bankslip
                  :dueat="dueAt"
                  :price="total"
                  :barcode="invoice.methods.bankslip.barcode"
                  :digitableLine="invoice.methods.bankslip.digitable_line"
                  :customerDocument="invoice.type === 'checkout' ? invoice.metadata.customer.document : invoice.tenant.document"
                  :customerName="invoice.type === 'checkout' ? invoice.metadata.customer.name : invoice.tenant.company_name"
                />
              </div>

              <div
                class="bdk-bg-white bdk-py-8 bdk-no-print"
                v-if="invoice.methods.credit_card"
              >
                <h2
                  v-text="__('Cartão de Crédito')"
                  class="bdk-text-xl bdk-tracking-tight"
                />

                <div
                  class="
                    bdk-flex
                    bdk-w-12/12
                    bdk-mt-4
                    bdk-justify-center
                    bdk-divide-x
                    bdk-divide-gray-200
                  "
                >
                  <div class="bdk-pr-4">
                    <CheckoutCard
                      class="flex-1"
                      :card-error="cardError"
                      :name.sync="credit_card.name"
                      :installment.sync="installment"
                      :number.sync="credit_card.number"
                      :validity.sync="credit_card.validity"
                      :code.sync="credit_card.security_code"
                      :price="
                        invoice.type === 'checkout' ? invoice.total : null
                      "
                      :error="cardErrors && cardErrors['last_name'] ? 'Verifique o nome do cartão' : null"
                    />

                    <div v-if="loading || disabled" class="bdk-mt-6">
                      <div class="bdk-rounded-md bdk-shadow">
                        <a
                          role="button"
                          :disabled="disabled ? true : false"
                          @click.prevent
                          class="
                            bdk-flex
                            bdk-items-center
                            bdk-justify-center
                            bdk-px-5
                            bdk-py-3
                            bdk-border
                            bdk-border-transparent
                            bdk-text-base
                            bdk-font-medium
                            bdk-rounded-md
                            bdk-text-white
                            bdk-bg-gray-800
                            hover:bdk-bg-gray-900
                            bdk-cursor-not-allowed
                          "
                        >
                          {{ loading ? loading : disabled }}

                          <div v-if="loading" class="bdk-payment__spinner" />
                        </a>
                      </div>
                    </div>

                    <div v-else class="bdk-mt-6">
                      <div class="bdk-rounded-md bdk-shadow">
                        <a
                          role="button"
                          @click.prevent="submit"
                          v-text="__('Pagar Agora')"
                          class="
                            bdk-flex
                            bdk-items-center
                            bdk-justify-center
                            bdk-px-5
                            bdk-py-3
                            bdk-border
                            bdk-border-transparent
                            bdk-text-base
                            bdk-font-medium
                            bdk-rounded-md
                            bdk-text-white
                            bdk-bg-gray-800
                            hover:bdk-bg-gray-900
                          "
                        />
                      </div>
                    </div>

                    <div
                      v-if="error"
                      v-text="error"
                      class="bdk-mt-3 bdk-text-sm bdk-text-red-600 bdk-w-80"
                    />
                  </div>

                  <div class="bdk-flex-grow bdk-pl-4">
                    <span
                      class="
                        bdk-block
                        bdk-text-xs
                        bdk-font-medium
                        bdk-mt-0.5
                        bdk-text-gray-500
                        bdk-uppercase
                      "
                      v-text="__('Resumo da fatura')"
                    />

                    <div class="bdk-divide-y bdk-divide-gray-200">
                      <div class="bdk-flex bdk-flex-col bdk-mt-1.5">
                        <div
                          :key="index"
                          class="bdk-flex bdk-justify-between bdk-items-start"
                          v-for="(
                            { description, price_cents }, index
                          ) in invoice.items"
                        >
                          <div
                            v-text="description"
                            class="
                              bdk-text-sm
                              bdk-mt-2
                              bdk-leading-6
                              bdk-font-medium
                              bdk-text-gray-500
                            "
                          />

                          <div
                            class="bdk-mt-2 bdk-flex bdk-flex-col bdk-space-y-2"
                          >
                            <span
                              v-text="formatValue(price_cents)"
                              class="bdk-text-sm bdk-text-gray-500"
                            />
                          </div>
                        </div>
                      </div>

                      <div
                        class="
                          bdk-flex bdk-justify-between bdk-items-start bdk-mt-2
                        "
                      >
                        <div
                          v-text="__('Total')"
                          class="
                            bdk-text-sm
                            bdk-mt-2
                            bdk-leading-6
                            bdk-font-medium
                            bdk-text-gray-500
                          "
                        />

                        <div class="bdk-mt-2 bdk-flex bdk-flex-col">
                          <span
                            class="bdk-text-sm bdk-text-gray-500"
                            v-text="`${installment}x de ${total}`"
                          />
                        </div>
                      </div>
                    </div>

                    <div class="bdk-flex bdk-flex-col bdk-mt-4">
                      <div class="bdk-flex bdk-mt-4">
                        <div
                          class="
                            bdk-w-9
                            h-9
                            flex
                            bdk-items-center bdk-justify-center bdk-mr-4
                            lg:bdk-mr-5
                            bdk-flex-shrink-0
                          "
                        >
                          <svg
                            class="bdk-w-9 bdk-h-9"
                            viewBox="0 0 91 84"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <g fill-rule="nonzero" fill="none">
                              <path
                                d="M82 17.9L47 .4c-1-.5-2.2-.5-3.2 0l-35 17.5c-1.3.6-2 1.8-2 3v52.6c0 5.8 4.7 10.5 10.5 10.5h56.2C79.3 84 84 79.3 84 73.5V21c0-1.3-.8-2.5-2-3.1z"
                                class="bdk-fill-current bdk-text-gray-200"
                              ></path>
                              <path
                                d="M63 35c-11.4 0-14.4-5-14.5-5-.6-1.3-1.8-2-3.1-2s-2.6.6-3.2 1.8c0 0-2.3 3.9-10.2 5l-4.2.2c-1.9 0-3.5 1.6-3.5 3.5v6.8c0 10 6.1 19 15.5 22.7l4.3 1.8c.8.3 1.8.3 2.6 0L51 68a24.5 24.5 0 0015.4-22.7v-6.8c0-2-1.5-3.5-3.5-3.5z"
                                class="bdk-fill-current bdk-text-gray-500"
                              ></path>
                              <path
                                d="M87.5 28c-.5 0-1-.1-1.6-.4L45.4 7.4 4.9 27.6c-1.7.7-3.7 0-4.5-1.6a3.5 3.5 0 011.3-4.6L43.8.4c1-.5 2.2-.5 3.2 0l42 21a3.5 3.5 0 01-1.5 6.6z"
                                class="bdk-fill-current bdk-text-gray-400"
                              ></path>
                            </g>
                          </svg>
                        </div>

                        <div class="bdk-flex bdk-flex-col">
                          <span
                            v-text="__('Pagamento seguro')"
                            class="
                              bdk-block
                              bdk-text-xs
                              bdk-font-medium
                              bdk-text-gray-900
                              bdk-mb-1
                            "
                          />

                          <span
                            v-text="__('Ambiente seguro e autenticado')"
                            class="bdk-block bdk-text-xs bdk-text-gray-500"
                          />
                        </div>
                      </div>

                      <div class="bdk-flex bdk-mt-4">
                        <div
                          class="
                            bdk-w-9
                            h-9
                            flex
                            bdk-items-center bdk-justify-center bdk-mr-4
                            lg:bdk-mr-5
                            bdk-flex-shrink-0
                          "
                        >
                          <svg
                            class="bdk-w-9 bdk-h-9"
                            viewBox="0 0 118 84"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <g fill-rule="nonzero" fill="none">
                              <path
                                d="M117.7 40C109.6 18.4 86.4 4.3 59 4.3 31.6 4.2 8.4 18.2.3 40.1L0 41.6v.4l.3 1.5c8.4 22 31.5 36 59 36s50.3-13.9 58.4-35.6c.2-.4.3-1 .3-1.5v-.8l-.3-1.5z"
                                class="bdk-fill-current bdk-text-gray-200"
                              ></path>
                              <ellipse
                                class="bdk-fill-current bdk-text-gray-400"
                                cx="59"
                                cy="42"
                                rx="25.3"
                                ry="25.2"
                              ></ellipse>
                              <path
                                d="M21 84a4.2 4.2 0 01-4.2-4.2c0-1.1.5-2.2 1.3-3L93.9 1.2a4.2 4.2 0 016 0 4.2 4.2 0 010 6L24.1 82.8c-.8.8-2 1.2-3 1.2z"
                                class="bdk-fill-current bdk-text-gray-500"
                              ></path>
                            </g>
                          </svg>
                        </div>

                        <div class="bdk-flex bdk-flex-col">
                          <span
                            v-text="__('Privacidade')"
                            class="
                              bdk-block
                              bdk-text-xs
                              bdk-font-medium
                              bdk-text-gray-900
                              bdk-mb-1
                            "
                          />

                          <span
                            v-text="__('Suas informações 100% seguras')"
                            class="bdk-block bdk-text-xs bdk-text-gray-500"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div
                class="
                  bdk-no-print
                  bdk-border-t bdk-border-gray-200 bdk-pt-8
                  lg:bdk-flex
                  bdk-flex-col
                  lg:bdk-items-center lg:bdk-justify-between
                  xl:bdk-mt-0
                "
              >
                <h3
                  v-text="__('Pagamentos processados pelo Iugu')"
                  class="
                    bdk-text-sm
                    bdk-font-semibold
                    bdk-text-gray-400
                    bdk-tracking-wider
                    bdk-uppercase
                  "
                />

                <p class="bdk-mt-2 bdk-text-sm bdk-text-gray-500">
                  Saiba mais sobre os nossos

                  <a
                    target="_blank"
                    class="bdk-font-medium bdk-text-gray-800"
                    href="https://salesflowcrm.movidesk.com/kb/article/405957/termos-de-uso"
                  >
                    Termos de Uso
                  </a>
                </p>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div
    v-else
    class="payment-success"
  >
    <div
      class="payment-success-box"
    >
      <CheckCircle
        color="#12BA7F"
        :size="80"
      />

      <h2
        class="payment-success-title"
      >
        {{ __('Pagamento efetuado com sucesso!') }}
      </h2>

      <span
        class="payment-success-info"
      >
        {{ __('Enviaremos uma mensagem de confirmação para o e-mail') }} {{ customerEmail }}.
      </span>

      <a
        href="/dashboard"
        class="payment-success-button"
      >
        {{ __('Ir para o Salesflow') }}
      </a>
    </div>
  </div>
</template>

<style lang="scss">
@import '../../sass/variables';
@import '../../sass/mixins';

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}


@media print {
  .bdk-no-print {
    display: none !important;
  }

  .bdk-on-print {
    display: block !important;
  }

  .bdk-no-padding {
    padding-top: 0px !important;
    padding-bottom: 0px !important;
  }
}

html {
  scroll-behavior: smooth !important;
}

.bdk-payment {
  &__spinner {
    width: 20px;
    height: 20px;
    margin-left: 8px;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    border: 3px solid rgba(0, 0, 0, 0.1);
    border-left-color: white;
  }

  &__pix {
    max-width: 190px;
    margin: 0px 40px 0px 0px;
  }

  &__barcode {

    &-img {
      width: 425px;
    }
  }

  &__bg {
    z-index: -1;
  }
}

.payment-success {
  display: flex;
  justify-content: center;

  &-box {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2rem;
    margin-top: 3.75rem;
    padding: 3.75rem 12.5rem;
    border-radius: 8px;
    background: $white;
    box-shadow: 0px 8px 24px 0px rgba(0, 0, 0, 0.10);
  }

  &-title {
    font-size: 2rem;
    font-weight: 700;
    font-family: Montserrat;
    line-height: normal;
    color: $salesflow-secondary-hover;
    margin: 0px;
  }

  &-info {
    @include salesflow-subtitle($color: $salesflow-font-gray, $font-weight: 500);
    max-width: 24rem;
    text-align: center;
  }

  &-button {
    padding: 1.5rem 2rem;
    background: $salesflow-font-dark;
    color: $white;
    font-size: 1.5rem;
    font-family: Montserrat;
    font-weight: 700;
    border-radius: 8px;
    text-decoration: none;

    &:hover {
      background: black;
    }
  }
}

</style>
